import { createSlice } from '@reduxjs/toolkit';
import { Customer, Address } from '../types/customer';
import { Pagination } from '../types/pagination';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  createCustomerApi,
  createAddressApi,
  getCustomerByIdApi,
  getCustomersApi,
  getAddressesByCustomerIdApi,
  getAddressByIdApi,
  updateAddressApi,
  updateCustomerApi,
} from '../services/customer/customer';
import { setAlert } from './commonSlice';
import { createQuery } from '../utils/constants';
import { Manufacturer } from '../types/manufacturer';
import { CurrencyInfo } from '../types/currency';

interface InitialState {
  customers: Customer[];
  pagination: Pagination | null;
  prevPage: string | null;
  nextPage: string | null;
  filter: any;
  loader: boolean;
  saveLoader: boolean;
  selectedCustomer: Customer | null;
  selectedCustomerAddresses: Address[] | null;
  selectedAddress: Address | null;
}

interface Filter {
  limit: number | null;
}

const initialState: InitialState = {
  customers: [],
  pagination: null,
  prevPage: null,
  nextPage: null,
  filter: null,
  loader: false,
  saveLoader: false,
  selectedCustomer: null,
  selectedCustomerAddresses: null,
  selectedAddress: null,
};

export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    getCustomers: (state, action: PayloadAction<any>) => {
      state.customers = action.payload;
    },
    setCustomers: (state, action: PayloadAction<any>) => {
      state.customers = action.payload;
    },
    setPagination: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filter = action.payload;
    },
    setLoader: (state, action: PayloadAction<any>) => {
      state.loader = action.payload;
    },
    setSaveLoader: (state, action: PayloadAction<any>) => {
      state.saveLoader = action.payload;
    },
    setSelectedCustomer: (state, action: PayloadAction<any>) => {
      state.selectedCustomer = action.payload;
    },
    setSelectedCustomerAddress: (state, action: PayloadAction<any>) => {
      state.selectedCustomerAddresses = action.payload;
    },
    setSelectedAddress: (state, action: PayloadAction<any>) => {
      state.selectedAddress = action.payload;
    },
  },
});

export const {
  getCustomers,
  setCustomers,
  setPagination,
  setFilter,
  setLoader,
  setSaveLoader,
  setSelectedCustomer,
  setSelectedCustomerAddress,
  setSelectedAddress,
} = customerSlice.actions;
export default customerSlice.reducer;

export const getCustomersAction = (payload: any) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setLoader(true));
    const { filter } = getState().customer;
    const queryParams = { ...filter, ...payload };
    const query = '?' + createQuery({ filter: queryParams });

    const response = await getCustomersApi(query);
    if (response.kind === 'ok') {
      const { pagination, included, data: list } = response.data;

      dispatch(setCustomers(list));
      dispatch(setPagination(pagination));
    }
  } catch (error) {
  } finally {
    dispatch(setLoader(false));
  }
};

export const createCustomerAction = (payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await createCustomerApi(payload);
    if (result?.kind === 'ok') {
      const { data } = result.data;
      dispatch(getCustomerAction(data.id));
      dispatch(setAlert('Customer has been created successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const updateCustomerAction = (id: any, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await updateCustomerApi(id, payload);
    if (result?.kind === 'ok') {
      dispatch(setAlert('Customer has been updated successfully', 'success'));
      cb?.(null, true);
    } else {
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const getCustomerAction = (id?: number | null) => async (dispatch: any, getState: any) => {
  if (id) {
    try {
      const response = await getCustomerByIdApi(id);
      if (response.kind === 'ok') {
        const { data } = response.data;

        dispatch(setSelectedCustomer(data));
      }
    } catch (error) {
    } finally {
      dispatch(setLoader(false));
    }
  } else {
    dispatch(setSelectedCustomer(null));
  }
};

export const applyFilter = (payload: any) => async (dispatch: any, getState: any) => {
  return dispatch(setFilter(payload));
};

export const getCustomerAddressesAction = (customerId: number) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setLoader(true));
    const { filter } = getState().product;
    const queryParams = { ...filter };
    // const query = '?currencyId=1&' + createQuery({ filter: queryParams });
    const query = '?' + createQuery({ filter: queryParams });

    const response = await getAddressesByCustomerIdApi(customerId, query);
    if (response.kind === 'ok') {
      const { pagination, included, data: list } = response.data;

      dispatch(setSelectedCustomerAddress(list));
    }
  } catch (error) {
  } finally {
    dispatch(setLoader(false));
  }
};

export const getCustomerAddressAction = (customerId: number, addressId: number) => async (dispatch: any, getState: any) => {
  try {
    const response = await getAddressByIdApi(customerId, addressId);
    if (response.kind === 'ok') {
      const { data } = response.data;

      dispatch(setSelectedAddress(data));
    }
  } catch (error) {
  } finally {
    dispatch(setLoader(false));
  }
};

export const createCustomerAddressAction = (customerId: any, payload: any, cb?: any) => async (dispatch: any) => {
  try {
    dispatch(setSaveLoader(true));
    const result = await createAddressApi(customerId, payload);
    if (result?.kind === 'ok') {
      dispatch(setAlert('Address has been created successfully', 'success'));
      cb?.(null, true);
    } else {
      const { error } = result.data;
      dispatch(setAlert(error.message, 'error'));
    }
  } catch (err) {
  } finally {
    dispatch(setSaveLoader(false));
  }
};

export const updateCustomerAddressAction =
  (customerId: any, addressId: number, payload: any, cb?: any) => async (dispatch: any) => {
    try {
      dispatch(setSaveLoader(true));
      const result = await updateAddressApi(customerId, addressId, payload);
      if (result?.kind === 'ok') {
        dispatch(setAlert('Address has been updated successfully', 'success'));
        cb?.(null, true);
      } else {
        const { error } = result.data;
        dispatch(setAlert(error.message, 'error'));
      }
    } catch (err) {
    } finally {
      dispatch(setSaveLoader(false));
    }
  };

export const getAddressAction = (payload: Address | null) => async (dispatch: any, getState: any) => {
  dispatch(setSelectedAddress(payload));
};
